import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { formatCurrency } from '@/lib/budget-utils';
import type { SalesBudget, ProjectType, BillingMilestone, RateCardEntry } from '@/types/budget.types';

interface EditSalesBudgetFormProps {
  salesBudget: SalesBudget;
  projectType: ProjectType;
  onChange: (changes: SalesBudget) => void;
}

export function EditSalesBudgetForm({
  salesBudget,
  projectType,
  onChange,
}: EditSalesBudgetFormProps) {
  const [contractValue, setContractValue] = useState(salesBudget?.contractValue || 0);
  const [milestones, setMilestones] = useState<BillingMilestone[]>(
    salesBudget?.milestones || []
  );
  const [rateCard, setRateCard] = useState<RateCardEntry[]>(
    salesBudget?.rateCard || []
  );
  const [monthlyRetainer, setMonthlyRetainer] = useState(
    salesBudget?.monthlyRetainer || 0
  );
  const [includedHours, setIncludedHours] = useState(
    salesBudget?.includedHours || 0
  );
  const [overageRate, setOverageRate] = useState(
    salesBudget?.overageRate || 0
  );

  useEffect(() => {
    onChange({
      ...salesBudget,
      contractValue,
      milestones,
      rateCard,
      monthlyRetainer,
      includedHours,
      overageRate,
    });
  }, [contractValue, milestones, rateCard, monthlyRetainer, includedHours, overageRate]);

  // Fixed Price specific handlers
  const addMilestone = () => {
    const newMilestone: BillingMilestone = {
      id: `milestone-${Date.now()}`,
      name: 'New Milestone',
      amount: 0,
      dueDate: new Date().toISOString().split('T')[0],
      invoiced: false,
      paid: false,
      status: 'pending',
    };
    setMilestones([...milestones, newMilestone]);
  };

  const updateMilestone = (index: number, field: keyof BillingMilestone, value: any) => {
    const updated = [...milestones];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    setMilestones(updated);
  };

  const removeMilestone = (index: number) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  // T&M specific handlers
  const addRateCardEntry = () => {
    const newEntry: RateCardEntry = {
      id: `rate-card-${Date.now()}`,
      role: 'New Role',
      billRate: 0,
      estimatedHours: 0,
      actualHours: 0,
      revenue: 0,
    };
    setRateCard([...rateCard, newEntry]);
  };

  const updateRateCardEntry = (index: number, field: keyof RateCardEntry, value: any) => {
    const updated = [...rateCard];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // Recalculate revenue
    if (field === 'billRate' || field === 'actualHours') {
      updated[index].revenue = updated[index].billRate * updated[index].actualHours;
    }
    
    setRateCard(updated);
  };

  const removeRateCardEntry = (index: number) => {
    setRateCard(rateCard.filter((_, i) => i !== index));
  };

  const totalMilestones = milestones.reduce((sum, m) => sum + (m.amount || 0), 0);
  const totalRateCardRevenue = rateCard.reduce((sum, r) => sum + (r.revenue || 0), 0);

  return (
    <div className="space-y-4">
      {/* Fixed Price Project */}
      {projectType === 'fixed-price' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Contract Value</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Total Contract Value</Label>
                  <Input
                    type="number"
                    value={contractValue}
                    onChange={(e) => setContractValue(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Total Milestones</Label>
                  <div className="h-10 flex items-center text-2xl font-bold">
                    {formatCurrency(totalMilestones)}
                  </div>
                  {totalMilestones !== contractValue && (
                    <p className="text-sm text-amber-600 mt-1">
                      ⚠️ Milestones don't match contract value
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Billing Milestones</CardTitle>
              <Button onClick={addMilestone} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Milestone
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {milestones.map((milestone, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="grid grid-cols-5 gap-3">
                    <div className="col-span-2">
                      <Label>Milestone Name</Label>
                      <Input
                        value={milestone.name}
                        onChange={(e) => updateMilestone(index, 'name', e.target.value)}
                        placeholder="Milestone name"
                      />
                    </div>
                    
                    <div>
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        value={milestone.amount}
                        onChange={(e) =>
                          updateMilestone(index, 'amount', parseFloat(e.target.value) || 0)
                        }
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={milestone.dueDate}
                        onChange={(e) => updateMilestone(index, 'dueDate', e.target.value)}
                      />
                    </div>
                    
                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeMilestone(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {milestones.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No milestones yet. Click "Add Milestone" to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Time & Materials Project */}
      {projectType === 'time-and-materials' && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Estimated Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Estimated Total Revenue</Label>
                  <Input
                    type="number"
                    value={contractValue}
                    onChange={(e) => setContractValue(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Not-to-exceed amount (optional)
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Rate Card Revenue</Label>
                  <div className="h-10 flex items-center text-2xl font-bold">
                    {formatCurrency(totalRateCardRevenue)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Rate Card</CardTitle>
              <Button onClick={addRateCardEntry} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Role
              </Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {rateCard.map((entry, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="grid grid-cols-6 gap-3">
                    <div className="col-span-2">
                      <Label>Role</Label>
                      <Input
                        value={entry.role}
                        onChange={(e) => updateRateCardEntry(index, 'role', e.target.value)}
                        placeholder="Role name"
                      />
                    </div>
                    
                    <div>
                      <Label>Bill Rate (£/hr)</Label>
                      <Input
                        type="number"
                        value={entry.billRate}
                        onChange={(e) =>
                          updateRateCardEntry(index, 'billRate', parseFloat(e.target.value) || 0)
                        }
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <Label>Est. Hours</Label>
                      <Input
                        type="number"
                        value={entry.estimatedHours}
                        onChange={(e) =>
                          updateRateCardEntry(
                            index,
                            'estimatedHours',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="0"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-muted-foreground">Revenue</Label>
                      <div className="h-10 flex items-center font-medium">
                        {formatCurrency(entry.revenue || 0)}
                      </div>
                    </div>
                    
                    <div className="flex items-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRateCardEntry(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {rateCard.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No rate card entries yet. Click "Add Role" to get started.
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Retainer Project */}
      {projectType === 'retainer' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Retainer Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Retainer Amount</Label>
                <Input
                  type="number"
                  value={monthlyRetainer}
                  onChange={(e) => setMonthlyRetainer(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Included Hours per Month</Label>
                <Input
                  type="number"
                  value={includedHours}
                  onChange={(e) => setIncludedHours(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Overage Rate (£/hr)</Label>
                <Input
                  type="number"
                  value={overageRate}
                  onChange={(e) => setOverageRate(parseFloat(e.target.value) || 0)}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Contract Duration (months)</Label>
                <Input
                  type="number"
                  value={contractValue / monthlyRetainer || 0}
                  onChange={(e) => {
                    const months = parseFloat(e.target.value) || 0;
                    setContractValue(months * monthlyRetainer);
                  }}
                  placeholder="0"
                />
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Total Contract Value</Label>
                  <div className="text-xl font-bold">{formatCurrency(contractValue)}</div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total Included Hours</Label>
                  <div className="text-xl font-bold">
                    {(includedHours * (contractValue / monthlyRetainer || 0)).toFixed(0)} hours
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
